"use client";

import { useEffect, useRef } from "react";
import { trackEvent } from "@/lib/client/analytics";

/**
 * Tracks scroll depth in search results at 25/50/75/100% thresholds.
 * Fires at most once per threshold per search session.
 * Resets when items change (new search).
 */
export function useScrollDepthTracking(totalItems: number, isLoading: boolean) {
  const firedThresholds = useRef(new Set<number>());
  const prevTotal = useRef(0);

  // Reset thresholds when a new search happens (total changes from a previous value)
  useEffect(() => {
    if (totalItems !== prevTotal.current && prevTotal.current > 0) {
      firedThresholds.current = new Set();
    }
    prevTotal.current = totalItems;
  }, [totalItems]);

  useEffect(() => {
    if (isLoading || totalItems === 0) return;

    const handleScroll = () => {
      try {
        const scrollTop = window.scrollY;
        const scrollHeight = document.documentElement.scrollHeight;
        const clientHeight = window.innerHeight;

        if (scrollHeight <= clientHeight) return;

        const scrollPercent = (scrollTop + clientHeight) / scrollHeight;
        const thresholds = [0.25, 0.5, 0.75, 1.0];

        for (const threshold of thresholds) {
          if (scrollPercent >= threshold && !firedThresholds.current.has(threshold)) {
            firedThresholds.current.add(threshold);
            trackEvent("scroll_depth", "engagement", {
              depth_percent: Math.round(threshold * 100),
              total_items: totalItems,
            });
          }
        }
      } catch {
        // Analytics must never affect scroll behavior
      }
    };

    // Debounce scroll handler
    let ticking = false;
    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [totalItems, isLoading]);
}
